// src/App.jsx
import React, { lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

// These are kept eager because they are needed on first paint / auth checks
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import Layout from './components/Layout';
import RequireAuth from './components/RequireAuth';
import PublicLayout from './components/PublicLayout';
import SessionBootstrap from './components/SessionBootstrap';
import LicenseInvalidPage from './pages/LicenseInvalidPage';

import { isAuthenticated, getNormalizedRole, targetPathForRole, shouldPromptRole } from './utils/auth';

// ── Lazy-loaded pages (each gets its own JS chunk) ──────────────────────────
const SetPassword = lazy(() => import('./pages/SetPassword'));
const RoleSelect = lazy(() => import('./pages/RoleSelect'));
const MyProfile = lazy(() => import('./pages/profile/MyProfile'));
const UserDetailsPage = lazy(() => import('./pages/users/UserDetailsPage'));

// Admin pages
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const BranchList = lazy(() => import('./pages/admin/BranchList'));
const BranchForm = lazy(() => import('./pages/admin/BranchForm'));
const UserList = lazy(() => import('./pages/admin/UserList'));
const UserForm = lazy(() => import('./pages/admin/UserForm'));
const ManagerList = lazy(() => import('./pages/admin/ManagerList'));
const ManagerForm = lazy(() => import('./pages/admin/ManagerForm'));
const CoachList = lazy(() => import('./pages/admin/CoachList'));
const CoachForm = lazy(() => import('./pages/admin/CoachForm'));
const StudentList = lazy(() => import('./pages/admin/StudentList'));
const StudentForm = lazy(() => import('./pages/admin/StudentForm'));
const AuditLogs = lazy(() => import('./pages/admin/AuditLogs'));
const AdminAdvancePayment = lazy(() => import('./pages/admin/AdvancePayment'));
const StudentImportPage = lazy(() => import('./pages/admin/StudentImportPage'));

const AdminLevelList = lazy(() => import('./pages/admin/levels/LevelList'));
const AdminLevelFormPage = lazy(() => import('./pages/admin/levels/LevelForm'));
const AdminBatchList = lazy(() => import('./pages/admin/batches/BatchList'));
const AdminBatchFormPage = lazy(() => import('./pages/admin/batches/BatchForm'));
const AdminEnrollmentList = lazy(() => import('./pages/admin/enrollments/EnrollmentList'));
const AdminEnrollmentFormPage = lazy(() => import('./pages/admin/enrollments/EnrollmentForm'));
const EnrollmentBillingPage = lazy(() => import('./pages/admin/enrollments/EnrollmentBillingPage'));
const EnrollmentDetail = lazy(() => import('./pages/admin/enrollments/EnrollmentDetail'));
const AdminSessionsList = lazy(() => import('./pages/admin/sessions/SessionsList'));
const AdminAttendanceMark = lazy(() => import('./pages/admin/sessions/AttendanceMark'));
const AdminAttendanceView = lazy(() => import('./pages/admin/sessions/AttendanceView'));
const FeePlans = lazy(() => import('./pages/admin/fees/FeePlans'));
const FeePlanForm = lazy(() => import('./pages/admin/fees/FeePlanForm'));
const InvoicesList = lazy(() => import('./pages/admin/invoices/InvoicesList'));
const DiscountsList = lazy(() => import('./pages/admin/discounts/DiscountsList'));
const DiscountForm = lazy(() => import('./pages/admin/discounts/DiscountForm'));

const CoachSessionsReport = lazy(() => import('./pages/admin/reports/CoachSessionsReport'));
const SessionsSummaryReport = lazy(() => import('./pages/admin/reports/SessionsSummaryReport'));
const BranchProgressReport = lazy(() => import('./pages/admin/reports/BranchProgressReport'));
const StudentStats = lazy(() => import('./pages/admin/reports/StudentStats'));
const PaymentsReport = lazy(() => import('./pages/admin/reports/PaymentsReport'));
const StudentLedger = lazy(() => import('./pages/admin/reports/StudentLedger'));
const PaymentsByReceiver = lazy(() => import('./pages/admin/reports/PaymentsByReceiver'));
const CoachRevenueReport = lazy(() => import('./pages/admin/reports/CoachRevenueReport'));
const CoachSettlementReport = lazy(() => import('./pages/admin/reports/CoachSettlementReport'));
const SessionAttendanceReport = lazy(() => import('./pages/admin/reports/SessionAttendanceReport'));
const ReportScheduler = lazy(() => import('./pages/admin/reports/ReportSchedulerNew'));
const MonthlyStatementControl = lazy(() => import('./pages/admin/reports/MonthlyStatementControl'));

// Manager pages
const ManagerDashboard = lazy(() => import('./pages/manager/ManagerDashboard'));
const ManagerBranches = lazy(() => import('./pages/manager/ManagerBranches'));
const ManagerCoachList = lazy(() => import('./pages/manager/ManagerCoachList'));
const ManagerStudentList = lazy(() => import('./pages/manager/ManagerStudentList'));
const ManagerLevelList = lazy(() => import('./pages/manager/levels/LevelList'));
const ManagerLevelFormPage = lazy(() => import('./pages/manager/levels/LevelForm'));
const ManagerBatchList = lazy(() => import('./pages/manager/batches/BatchList'));
const ManagerBatchFormPage = lazy(() => import('./pages/manager/batches/BatchForm'));
const ManagerEnrollmentList = lazy(() => import('./pages/manager/enrollments/EnrollmentList'));
const ManagerEnrollmentFormPage = lazy(() => import('./pages/manager/enrollments/EnrollmentForm'));
const ManagerSessionsList = lazy(() => import('./pages/manager/sessions/SessionsList'));
const ManagerAttendanceMark = lazy(() => import('./pages/manager/sessions/AttendanceMark'));
const ManagerAttendanceView = lazy(() => import('./pages/manager/sessions/AttendanceView'));
const ManagerFeePlans = lazy(() => import('./pages/manager/fees/FeePlans'));
const ManagerInvoicesList = lazy(() => import('./pages/manager/fees/InvoicesList'));

// Coach pages
const CoachDashboard = lazy(() => import('./pages/coach/CoachDashboard'));
const CoachLevelList = lazy(() => import('./pages/coach/levels/LevelList'));
const CoachEnrollmentList = lazy(() => import('./pages/coach/enrollments/EnrollmentList'));
const CoachEnrollmentFormPage = lazy(() => import('./pages/coach/enrollments/EnrollmentForm'));
const CoachSessionsList = lazy(() => import('./pages/coach/sessions/SessionsList'));
const CoachAttendanceMark = lazy(() => import('./pages/coach/sessions/AttendanceMark'));
const CoachAttendanceView = lazy(() => import('./pages/coach/sessions/AttendanceView'));
const CoachStudentsList = lazy(() => import('./pages/coach/students/StudentsList'));
const CoachFeePlans = lazy(() => import('./pages/coach/fees/FeePlans'));
const CoachInvoicesList = lazy(() => import('./pages/coach/fees/InvoicesList'));

// Student pages
const StudentDashboard = lazy(() => import('./pages/student/StudentDashboard'));
const StudentAttendance = lazy(() => import('./pages/student/StudentAttendance'));
const StudentSessions = lazy(() => import('./pages/student/StudentSessions'));
const StudentEnrollments = lazy(() => import('./pages/student/StudentEnrollments'));
const MyEnrollments = lazy(() => import('./pages/student/MyEnrollments'));
const MyCredits = lazy(() => import('./pages/student/MyCredits'));
const SessionStatement = lazy(() => import('./pages/student/SessionStatement'));
const StudentWallet = lazy(() => import('./pages/student/StudentWallet'));

// Shared calendar and holiday pages
const SessionCalendar = lazy(() => import('./pages/shared/SessionCalendar'));
const HolidayManagement = lazy(() => import('./pages/admin/HolidayManagement'));




function App() {
    const token = localStorage.getItem('token');

    return (
        <BrowserRouter>
            <SessionBootstrap />
            <Routes>
                <Route path="/license-invalid" element={<LicenseInvalidPage />} />

                {/* Root -> login or role dashboard (prompt if multi-role & none selected) */}
                <Route
                    path="/"
                    element={
                        isAuthenticated()
                            ? (shouldPromptRole()
                                ? <Navigate to="/select-role" replace />
                                : <Navigate to={targetPathForRole(getNormalizedRole())} replace />)
                            : <Navigate to="/login" replace />
                    }
                />

                {/* Public layout (login + set password) */}
                <Route path="/" element={<PublicLayout />}>
                    <Route
                        path="login"
                        element={
                            isAuthenticated()
                                ? (shouldPromptRole()
                                    ? <Navigate to="/select-role" replace />
                                    : <Navigate to={targetPathForRole(getNormalizedRole())} replace />)
                                : <Login />
                        }
                    />
                    <Route path="set-password/:token" element={<SetPassword />} />
                    <Route path="forgot-password" element={<ForgotPassword />} />
                </Route>

                {/* Mount Select Role INSIDE Layout (no allowedRoles) so header/footer appear */}
                <Route
                    element={
                        <RequireAuth>
                            <Layout />
                        </RequireAuth>
                    }
                >
                    <Route path="/select-role" element={<RoleSelect />} />
                    <Route path="/users/:userId" element={<UserDetailsPage />} />

                </Route>

                {/* Admin (ADMIN only) */}
                <Route
                    path="/admin/*"
                    element={
                        <RequireAuth allowedRoles={['ADMIN']}>
                            <Layout />
                        </RequireAuth>
                    }
                >
                    <Route index element={<AdminDashboard />} />
                    <Route path="users" element={<UserList />} />
                    <Route path="users/new" element={<UserForm />} />
                    <Route path="users/:id" element={<UserForm />} />
                    <Route path="branches" element={<BranchList />} />
                    <Route path="branches/new" element={<BranchForm />} />
                    <Route path="branches/:id" element={<BranchForm />} />
                    <Route path="managers" element={<ManagerList />} />
                    <Route path="managers/new" element={<ManagerForm />} />
                    <Route path="managers/:id" element={<ManagerForm />} />
                    <Route path="coaches" element={<CoachList />} />
                    <Route path="students" element={<StudentList />} />

                    <Route path="coaches/new" element={<CoachForm />} />
                    <Route path="coaches/:id" element={<CoachForm />} />
                    <Route path="students/new" element={<StudentForm />} />
                    <Route path="students/bulk-import" element={<StudentImportPage />} />
                    <Route path="students/:id" element={<StudentForm />} />

                    <Route path="levels" element={<AdminLevelList />} />
                    <Route path="levels/new" element={<AdminLevelFormPage />} />
                    <Route path="levels/:id/edit" element={<AdminLevelFormPage />} />

                    <Route path="batches" element={<AdminBatchList />} />
                    <Route path="batches/new" element={<AdminBatchFormPage />} />
                    <Route path="batches/:id/edit" element={<AdminBatchFormPage />} />
                    <Route path="batches/:id/sessions" element={<AdminSessionsList />} />
                    <Route path="profile" element={<MyProfile />} />

                    <Route path="enrollments" element={<AdminEnrollmentList />} />
                    <Route path="enrollments/new" element={<AdminEnrollmentFormPage />} />
                    <Route path="enrollments/:id/edit" element={<AdminEnrollmentFormPage />} />
                    <Route path="enrollments/:id/billing" element={<EnrollmentBillingPage />} />
                    <Route path="enrollments/:id" element={<EnrollmentDetail />} />

                    <Route path="discounts" element={<DiscountsList />} />
                    <Route path="discounts/new" element={<DiscountForm />} />
                    <Route path="discounts/:id/edit" element={<DiscountForm />} />

                    <Route path="sessions" element={<AdminSessionsList />} />
                    <Route path="sessions/:sessionId/attendance" element={<AdminAttendanceMark />} />
                    <Route path="sessions/:sessionId/attendance/view" element={<AdminAttendanceView />} />

                    <Route path="reports/coaches/sessions" element={<CoachSessionsReport />} />
                    <Route path="reports/sessions-summary" element={<SessionsSummaryReport />} />
                    <Route path="reports/branches/progress" element={<BranchProgressReport />} />
                    <Route path="reports/students/stats" element={<StudentStats />} />
                    <Route path="reports/finance/payments" element={<PaymentsReport />} />
                    <Route path="reports/student-ledger" element={<StudentLedger />} />
                    <Route path="reports/payments-by-receiver" element={<PaymentsByReceiver />} />
                    <Route path="reports/coach-revenue" element={<CoachRevenueReport />} />
                    <Route path="reports/coach-settlement" element={<CoachSettlementReport />} />
                    <Route
                        path="reports/session-statement"
                        element={<SessionStatement />}
                    />

                    <Route
                        path="reports/session-attendance"
                        element={<SessionAttendanceReport />}
                    />

                    <Route
                        path="reports/scheduler"
                        element={<ReportScheduler />}
                    />

                    <Route
                        path="reports/monthly-statements"
                        element={<MonthlyStatementControl />}
                    />

                    <Route path="fees/plans" element={<FeePlans />} />
                    <Route path="fees/plans/new" element={<FeePlanForm />} />
                    <Route path="invoices" element={<InvoicesList />} />

                    <Route path="advance-payment" element={<AdminAdvancePayment />} />

                    <Route path="audit-logs" element={<AuditLogs />} />

                    <Route path="student-import" element={<StudentImportPage />} />

                    <Route path="calendar" element={<SessionCalendar roleProp="ADMIN" />} />
                    <Route path="holidays" element={<HolidayManagement />} />

                </Route>

                {/* Manager (MANAGER only) */}
                <Route
                    path="/manager/*"
                    element={
                        <RequireAuth allowedRoles={['MANAGER']}>
                            <Layout />
                        </RequireAuth>
                    }
                >
                    <Route index element={<ManagerDashboard />} />
                    <Route path="branches" element={<ManagerBranches />} />
                    <Route path="coaches" element={<ManagerCoachList />} />
                    <Route path="students" element={<ManagerStudentList />} />
                    <Route path="students/bulk-import" element={<StudentImportPage />} />
                    <Route path="students/new" element={<StudentForm managerMode />} />
                    <Route path="students/:id" element={<StudentForm managerMode />} />

                    <Route path="levels" element={<ManagerLevelList />} />
                    <Route path="levels/new" element={<ManagerLevelFormPage />} />
                    <Route path="levels/:id/edit" element={<ManagerLevelFormPage />} />

                    <Route path="batches" element={<ManagerBatchList />} />
                    <Route path="batches/new" element={<ManagerBatchFormPage />} />
                    <Route path="batches/:id/edit" element={<ManagerBatchFormPage />} />
                    <Route path="batches/:id/sessions" element={<ManagerSessionsList />} />
                    <Route path="profile" element={<MyProfile />} />

                    <Route path="enrollments" element={<ManagerEnrollmentList />} />
                    <Route path="enrollments/new" element={<ManagerEnrollmentFormPage />} />

                    <Route path="fees/plans" element={<ManagerFeePlans />} />
                    <Route path="invoices" element={<ManagerInvoicesList />} />

                    <Route path="sessions" element={<ManagerSessionsList />} />
                    <Route path="sessions/:sessionId/attendance" element={<ManagerAttendanceMark />} />
                    <Route path="sessions/:sessionId/attendance/view" element={<ManagerAttendanceView />} />

                    <Route
                        path="reports/session-statement"
                        element={<SessionStatement />}
                    />
                    <Route
                        path="reports/session-attendance"
                        element={<SessionAttendanceReport />}
                    />
                    <Route
                        path="reports/coach-revenue"
                        element={<CoachRevenueReport role="MANAGER" />}
                    />

                    <Route path="advance-payment" element={<AdminAdvancePayment role="MANAGER" />} />

                    <Route path="calendar" element={<SessionCalendar roleProp="MANAGER" />} />


                </Route>

                {/* Coach (COACH only) */}
                <Route
                    path="/coach/*"
                    element={
                        <RequireAuth allowedRoles={['COACH']}>
                            <Layout />
                        </RequireAuth>
                    }
                >
                    <Route index element={<CoachDashboard />} />
                    {/* add coach-specific routes here */}

                    <Route path="levels" element={<CoachLevelList />} />

                    <Route path="profile" element={<MyProfile />} />

                    <Route path="enrollments" element={<CoachEnrollmentList />} />
                    <Route path="enrollments/new" element={<CoachEnrollmentFormPage />} />
                    <Route path="sessions" element={<CoachSessionsList />} />
                    <Route path="sessions/:sessionId/attendance" element={<CoachAttendanceMark />} />
                    <Route path="sessions/:sessionId/attendance/view" element={<CoachAttendanceView />} />
                    <Route path="students" element={<CoachStudentsList />} />
                    <Route path="students/bulk-import" element={<StudentImportPage />} />

                    <Route path="fees/plans" element={<CoachFeePlans />} />
                    <Route path="invoices" element={<CoachInvoicesList />} />

                    <Route
                        path="reports/session-statement"
                        element={<SessionStatement />}
                    />

                    <Route path="calendar" element={<SessionCalendar roleProp="COACH" />} />

                </Route>

                {/* Student (STUDENT only) */}
                <Route
                    path="/student/*"
                    element={
                        <RequireAuth allowedRoles={['STUDENT']}>
                            <Layout />
                        </RequireAuth>
                    }
                >
                    <Route index element={<StudentDashboard />} />
                    <Route path="attendance" element={<StudentAttendance />} />
                    <Route path="sessions" element={<StudentSessions />} />
                    <Route path="enrollments" element={<StudentEnrollments />} />
                    <Route path="profile" element={<MyProfile />} />

                    <Route path="credits" element={<MyCredits />} />
                    <Route path="wallet" element={<StudentWallet />} />
                    <Route path="session-statement" element={<SessionStatement />} />

                    <Route path="calendar" element={<SessionCalendar roleProp="STUDENT" />} />

                </Route>

                {/* Catch-all */}
                <Route
                    path="*"
                    element={
                        isAuthenticated()
                            ? (shouldPromptRole()
                                ? <Navigate to="/select-role" replace />
                                : <Navigate to={targetPathForRole(getNormalizedRole())} replace />)
                            : <Navigate to="/login" replace />
                    }
                />
            </Routes>
        </BrowserRouter>
    );
}

export default App;
